import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { reportingService } from '../../../services/reportingService';

const TechnicianDetailModal = ({ technician, dateRange, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState(null);

  useEffect(() => {
    if (technician?.technicianId) {
      loadTechnicianDetails();
    }
  }, [technician]);

  const loadTechnicianDetails = async () => {
    setLoading(true);
    try {
      const data = await reportingService?.getTechnicianDetailedPerformance(
        technician?.technicianId,
        dateRange?.startDate,
        dateRange?.endDate
      );
      setDetails(data);
    } catch (error) {
      console.error('Error loading technician details:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return `$${value?.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr)?.toLocaleDateString('es-MX', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-elevation-3 max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Icon name="User" size={24} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-heading font-bold text-foreground">
                {technician?.technicianName}
              </h2>
              <p className="text-sm text-muted-foreground">
                Desempeño Detallado
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <Icon name="Loader2" className="w-8 h-8 text-primary animate-spin" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Metrics Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-primary/5 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="DollarSign" size={18} className="text-primary" />
                    <span className="text-sm text-muted-foreground">Ingresos Totales</span>
                  </div>
                  <p className="text-2xl font-bold text-foreground">
                    {formatCurrency(details?.totalRevenue || 0)}
                  </p>
                </div>
                <div className="bg-success/5 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="CheckCircle" size={18} className="text-success" />
                    <span className="text-sm text-muted-foreground">Órdenes Completadas</span>
                  </div>
                  <p className="text-2xl font-bold text-foreground">
                    {details?.completedOrders || 0}
                  </p>
                </div>
                <div className="bg-warning/5 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="TrendingUp" size={18} className="text-warning" />
                    <span className="text-sm text-muted-foreground">Ticket Promedio</span>
                  </div>
                  <p className="text-2xl font-bold text-foreground">
                    {formatCurrency(details?.averageTicket || 0)}
                  </p>
                </div>
              </div>

              {/* Work Orders List */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Icon name="FileText" size={20} />
                  Órdenes de Trabajo
                </h3>
                <div className="space-y-3">
                  {details?.workOrders?.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      No hay órdenes de trabajo en este período
                    </p>
                  ) : (
                    details?.workOrders?.map((order) => (
                      <div
                        key={order?.id}
                        className="bg-muted/30 rounded-lg p-4 hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-semibold text-foreground">
                              OT-{order?.folioOt}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {formatDate(order?.fechaIngreso)}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-success">
                              {formatCurrency(order?.total)}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {order?.itemCount || 0} servicios
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Icon name="Car" size={14} />
                            {order?.vehicleInfo}
                          </span>
                          <span className="flex items-center gap-1">
                            <Icon name="User" size={14} />
                            {order?.clientName}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TechnicianDetailModal;